import type { Patient, Reference } from '@medplum/fhirtypes'
import { chunk, isEqual, uniqBy } from 'lodash-es'
import type { SeenMedplumClient } from '#src/client.ts'
import { invariant } from '#src/invariant.ts'
import { ExtendedDiagnosticReport } from '#src/models/diagnosticReport.ts'
import { ExtendedDocumentReference } from '#src/models/document-references/documentReferences.ts'
import {
  type LabCopyMember,
  LabCopyKind,
  NO_LAB_COPY_GROUP,
  planLabCopyGroup,
} from '#src/models/document-references/labCopies.ts'
import { DocumentReviewTask } from '#src/models/tasks/standard/documentReviewTask.ts'

type LabCopyClient = Pick<
  SeenMedplumClient,
  | 'searchResourceClass'
  | 'searchOneResourceClass'
  | 'searchResourceClassPagesAll'
  | 'patchResourceInstance'
>

export interface LabCopyTarget {
  participant: Reference<Patient>
  accession: string
  // Documents the caller already holds that point at this group. Reconcile uses
  // them even when no search reaches them, so a copy that triggered the run is
  // never lost to a lookup that cannot find it.
  documents?: ExtendedDocumentReference[]
}

export const labCopyTargetKey = ({
  participant,
  accession,
}: LabCopyTarget): string => `${participant.reference}/${accession}`

// Which (participant, accession) groups a set of documents belongs to or just
// left. A copy already carrying a group tag names its group directly, which is
// also how a voided copy finds the group to be released from. The tag is paired
// with the participant the group was formed under, so a copy moved to another
// participant still wakes the group it left as well as the one it joined.
// Health Gorilla's copies carry no accession, so they resolve through their
// report; that is also how a PDF delivered with no participant learns whose it
// is. Each target carries the documents that led to it.
export async function findLabCopyTargets(
  medplum: Pick<LabCopyClient, 'searchResourceClass'>,
  documents: ExtendedDocumentReference[]
): Promise<LabCopyTarget[]> {
  const targets = new Map<string, LabCopyTarget>()
  const add = (target: LabCopyTarget, document?: ExtendedDocumentReference) => {
    const key = labCopyTargetKey(target)
    const existing = targets.get(key) ?? { ...target, documents: [] }
    if (document && !existing.documents?.includes(document)) {
      existing.documents?.push(document)
    }
    targets.set(key, existing)
  }

  const documentsByHealthGorillaId = new Map<
    string,
    ExtendedDocumentReference[]
  >()
  for (const document of documents) {
    const { subject, labCopyGroup } = document
    if (labCopyGroup) {
      const owner = document.labCopyGroupParticipant ?? subject?.reference
      if (owner) {
        add(
          { participant: { reference: owner }, accession: labCopyGroup },
          document
        )
      }
      if (subject?.reference && subject.reference !== owner) {
        add(
          {
            participant: { reference: subject.reference },
            accession: labCopyGroup,
          },
          document
        )
      }
    }
    const accession = document.elationLabAccession
    if (subject?.reference && accession) {
      add(
        { participant: { reference: subject.reference }, accession },
        document
      )
    }
    const healthGorillaId = document.healthGorillaReportId
    if (healthGorillaId) {
      documentsByHealthGorillaId.set(healthGorillaId, [
        ...(documentsByHealthGorillaId.get(healthGorillaId) ?? []),
        document,
      ])
    }
  }

  for (const ids of chunk([...documentsByHealthGorillaId.keys()], 50)) {
    const reports = await medplum.searchResourceClass(
      ExtendedDiagnosticReport,
      ExtendedDiagnosticReport.byHealthGorillaIds(ids)
    )
    for (const report of reports) {
      const target = labCopyTargetForReport(report)
      const reportDocuments = documentsByHealthGorillaId.get(
        report.healthGorillaId ?? ''
      )
      if (target) {
        add(target)
        for (const document of reportDocuments ?? []) {
          add(target, document)
        }
      }
    }
  }
  return [...targets.values()]
}

export function labCopyTargetForReport(
  report: ExtendedDiagnosticReport
): LabCopyTarget | undefined {
  const accession = report.questAccession
  const reference = report.subject?.reference
  return accession && reference
    ? { participant: { reference }, accession }
    : undefined
}

export interface ReconcileLabCopiesResult {
  // References of the documents written. Empty when the group was already right.
  written: string[]
  // Documents that carried no participant and were filed to the report's.
  filed: string[]
}

const documentReference = (document: ExtendedDocumentReference): string =>
  `DocumentReference/${document.id}`

async function findCandidates(
  medplum: LabCopyClient,
  participant: Reference<Patient>,
  accession: string,
  report: ExtendedDiagnosticReport | null,
  held: ExtendedDocumentReference[]
): Promise<ExtendedDocumentReference[]> {
  const queries = [
    ExtendedDocumentReference.byLabCopyGroup(participant, accession),
    ExtendedDocumentReference.byQuestAccession(participant, accession),
    ExtendedDocumentReference.byLabCopyGroupVoided(participant, accession),
  ]
  const healthGorillaId = report?.healthGorillaId
  if (report && healthGorillaId) {
    queries.push(
      ExtendedDocumentReference.byHealthGorillaId(healthGorillaId),
      ...ExtendedDocumentReference.byDiagnosticReportSources({
        reference: `DiagnosticReport/${report.id}`,
      })
    )
  }
  const search = async (query: (typeof queries)[number]) =>
    medplum.searchResourceClassPagesAll(ExtendedDocumentReference, query)
  const searched = (await Promise.all(queries.map(search))).flat()
  // A document the caller holds is a snapshot from when its event fired, and may
  // have been voided or moved since. Those the searches did not return are read
  // again by id, so only their current version can take part; one that is no
  // longer a current copy then drops out of the group instead of being chosen
  // as primary.
  const heldIds = held.flatMap((document) =>
    searched.some((other) => other.id === document.id) ? [] : [document.id]
  )
  const reread =
    heldIds.length > 0
      ? await medplum.searchResourceClass(ExtendedDocumentReference, {
          _id: heldIds,
          _count: heldIds.length,
        })
      : []
  const found = uniqBy([...searched, ...reread], (document) => document.id)

  // Health Gorilla's PDF is cited by Observations, which is how the queries
  // above reach it, but a result whose Observations omit it would never join.
  // Without one in hand, look through the participant's Health Gorilla documents.
  const hasPdf = found.some(
    (document) =>
      document.labCopyKind === LabCopyKind.QUEST_PDF &&
      document.healthGorillaReportId === healthGorillaId
  )
  if (!healthGorillaId || hasPdf) {
    return found
  }
  return uniqBy(
    [
      ...found,
      ...(
        await search(
          ExtendedDocumentReference.byHealthGorillaParticipant(participant)
        )
      ).filter(
        (document) => document.healthGorillaReportId === healthGorillaId
      ),
    ],
    (document) => document.id
  )
}

async function referencesWithOpenReview(
  medplum: LabCopyClient,
  documents: ExtendedDocumentReference[]
): Promise<Set<string>> {
  const open = new Set<string>()
  for (const batch of chunk(documents.map(documentReference), 100)) {
    const tasks = await medplum.searchResourceClass(
      DocumentReviewTask,
      DocumentReviewTask.byDocumentsActive(
        batch.map((reference) => ({ reference }))
      )
    )
    for (const task of tasks) {
      if (task.focus?.reference) {
        open.add(task.focus.reference)
      }
    }
  }
  return open
}

// Brings every copy of one Quest result in one participant to the state the
// planner wants, writing only the documents that differ. Safe to run any number
// of times and from any trigger: the second run of an unchanged group writes
// nothing. A copy delivered with no participant is filed to its report's
// participant first, so it takes part in the group instead of sitting in an
// unfiled queue beside it.
export async function reconcileLabCopies(
  medplum: LabCopyClient,
  participant: Reference<Patient>,
  accession: string,
  held: ExtendedDocumentReference[] = []
): Promise<ReconcileLabCopiesResult> {
  const participantReference = participant.reference
  invariant(participantReference, 'A lab copy group needs a participant')
  const report = await medplum.searchOneResourceClass(
    ExtendedDiagnosticReport,
    ExtendedDiagnosticReport.byParticipantAndAccession(participant, accession)
  )
  const candidates = await findCandidates(
    medplum,
    participant,
    accession,
    report,
    held
  )

  // A document with another participant's subject is never moved or grouped
  // here, even when it cites this report.
  const ours = candidates.filter(
    (document) =>
      !document.subject || document.subject.reference === participant.reference
  )

  const isOfThisResult = (document: ExtendedDocumentReference): boolean => {
    switch (document.labCopyKind) {
      case undefined:
        return false
      case LabCopyKind.ELATION:
        return document.elationLabAccession === accession
      default:
        return (
          report !== null &&
          document.healthGorillaReportId === report.healthGorillaId
        )
    }
  }
  const members = ours.filter(isOfThisResult)

  const openReview = await referencesWithOpenReview(medplum, members)
  const plan = planLabCopyGroup(
    accession,
    members.flatMap((document): LabCopyMember[] => {
      const kind = document.labCopyKind
      return kind
        ? [
            {
              reference: documentReference(document),
              kind,
              received: document.dateStringWithFallback ?? '',
              hasOpenReviewTask: openReview.has(documentReference(document)),
              folder: document.folderId,
            },
          ]
        : []
    })
  )
  // Tagged documents that no longer belong (voided, retyped, now spanning
  // several accessions) are released.
  for (const document of ours) {
    if (
      !plan.has(documentReference(document)) &&
      document.labCopyGroup === accession
    ) {
      plan.set(documentReference(document), NO_LAB_COPY_GROUP)
    }
  }

  const written: string[] = []
  const filed: string[] = []
  for (const document of ours) {
    const reference = documentReference(document)
    const wanted = plan.get(reference)
    if (!wanted) {
      continue
    }
    const needsFiling = !document.subject && members.includes(document)
    const wantedParticipant = wanted.group ? participantReference : undefined
    if (
      !needsFiling &&
      isEqual(wanted, document.labCopyState) &&
      document.labCopyGroupParticipant === wantedParticipant
    ) {
      continue
    }
    await medplum.patchResourceInstance(document, (draft) => {
      if (needsFiling) {
        draft.subject = participant
        draft.markFiledByLabCopyReconciler()
      }
      draft.setLabCopyState(wanted, participantReference)
    })
    written.push(reference)
    if (needsFiling) {
      filed.push(reference)
    }
  }
  return { written, filed }
}
